import { useState } from 'react';
import { useMcpAuditLogs } from '../../api/useMcpAuditLogs';
import { McpAuditLogItem } from '../../api/types';

export default function McpAuditLogTable() {
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, isError, refetch, isFetching } = useMcpAuditLogs(page, pageSize);

  const logs = data?.results || [];
  const pagination = data?.pagination || {
    page: 1,
    page_size: pageSize,
    total: logs.length,
    has_next: false,
  };

  const totalPages = Math.max(1, Math.ceil((pagination.total || 1) / pageSize));

  const formatTimestamp = (isoString?: string) => {
    if (!isoString) return '—';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return isoString;
    }
  };

  const renderClientBadge = (client?: string | null) => {
    const raw = (client || 'AI Agent').trim();
    if (/claude/i.test(raw)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
          Claude Desktop
        </span>
      );
    }
    if (/cursor/i.test(raw)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
          <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
          Cursor
        </span>
      );
    }
    if (/token|static/i.test(raw)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
          Static Token
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-gray-100 text-gray-700 dark:bg-strokedark dark:text-bodydark border border-gray-200 dark:border-strokedark">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        {raw}
      </span>
    );
  };

  const renderStatusBadge = (status: McpAuditLogItem['status']) => {
    const s = (status || '').toLowerCase();
    if (s === 'success' || s === '200' || s === 'ok') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Success
        </span>
      );
    }
    if (s === 'rate_limited' || s === '429') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Rate Limited
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
        Error
      </span>
    );
  };

  const renderLatency = (ms?: number | null) => {
    if (ms === undefined || ms === null) return <span className="text-brand-soft">—</span>;
    let colorClass = 'text-emerald-600 dark:text-emerald-400';
    if (ms > 800) colorClass = 'text-rose-600 dark:text-rose-400';
    else if (ms > 300) colorClass = 'text-amber-600 dark:text-amber-400';

    return <span className={`font-mono text-xs font-semibold ${colorClass}`}>{ms}ms</span>;
  };

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-6 shadow-card">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-brand-line dark:border-strokedark">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 flex items-center justify-center flex-shrink-0 border border-brand-vib/20">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-brand-navy dark:text-white font-mono">
                Activity & Security Audit Log
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 uppercase tracking-wider border border-brand-vib/20">
                MCP / API
              </span>
            </div>
            <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
              Live audit trail of AI agent tool calls, API requests, and autonomous executions.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-semibold bg-white dark:bg-strokedark text-brand-navy dark:text-white border border-brand-line dark:border-strokedark hover:bg-brand-bg transition-colors cursor-pointer shadow-xs disabled:opacity-60"
            title="Refresh audit log"
          >
            <span className={isFetching ? 'animate-spin' : ''}>↻</span>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-brand-blue-tint/60 dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark">
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-44">
                Timestamp
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-40">
                Client
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                Action / Tool
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-28 text-right">
                Latency
              </th>
              <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-32 text-center">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <tr key={idx} className="animate-pulse">
                  <td className="py-3.5 px-4">
                    <div className="h-3.5 bg-gray-200 dark:bg-strokedark rounded w-28" />
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="h-5 bg-gray-200 dark:bg-strokedark rounded-lg w-24" />
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="h-4 bg-gray-200 dark:bg-strokedark rounded w-36" />
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <div className="h-3.5 bg-gray-200 dark:bg-strokedark rounded w-12 ml-auto" />
                  </td>
                  <td className="py-3.5 px-4 text-center">
                    <div className="h-5 bg-gray-200 dark:bg-strokedark rounded-full w-20 mx-auto" />
                  </td>
                </tr>
              ))
            ) : isError || logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 flex items-center justify-center mx-auto mb-2 border border-brand-vib/20">
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2zm0-4H7V7h10v2z" />
                    </svg>
                  </div>
                  <p className="text-xs font-bold text-brand-navy dark:text-white font-mono">
                    No activity logged yet
                  </p>
                  <p className="text-[11.5px] text-brand-muted dark:text-bodydark max-w-sm mx-auto mt-1">
                    When AI agents (Claude Desktop, Cursor, etc.) query your MCP server, requests and tool executions will appear here in real-time.
                  </p>
                </td>
              </tr>
            ) : (
              logs.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-brand-bg/60 dark:hover:bg-strokedark/30 transition-colors"
                >
                  <td className="py-3 px-4 text-brand-navy dark:text-white">
                    {formatTimestamp(item.timestamp)}
                  </td>
                  <td className="py-3 px-4">
                    {renderClientBadge(item.client_name)}
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 rounded bg-brand-bg dark:bg-boxdark border border-brand-line dark:border-strokedark font-mono text-brand-navy dark:text-white font-semibold text-[11.5px]">
                      {item.tool_name}
                    </span>
                    {item.error_message && (
                      <span className="block text-[10.5px] text-rose-600 dark:text-rose-400 mt-1 truncate max-w-md">
                        {item.error_message}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {renderLatency(item.latency_ms)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {renderStatusBadge(item.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {logs.length > 0 && (
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-brand-line dark:border-strokedark text-xs font-mono">
          <span className="text-brand-soft">
            Page {page} of {totalPages} ({pagination.total || logs.length} events)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="px-3 py-1.5 rounded-lg border border-brand-line dark:border-strokedark bg-white dark:bg-strokedark text-brand-navy dark:text-white hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-xs"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => (pagination.has_next || p < totalPages ? p + 1 : p))}
              disabled={!pagination.has_next && page >= totalPages}
              className="px-3 py-1.5 rounded-lg border border-brand-line dark:border-strokedark bg-white dark:bg-strokedark text-brand-navy dark:text-white hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer shadow-xs"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
